import React, { useState } from 'react';
import { 
  CardNumberElement, 
  CardExpiryElement, 
  CardCvcElement,
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import { saveBookingToSupabase } from './services/bookingService';

// Supabase Edge Function URL
const SUPABASE_FUNCTION_URL = 'https://wnsyglbyunngvlcpdhju.supabase.co/functions/v1';
// Supabase anon key
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Induc3lnbGJ5dW5uZ3ZsY3BkaGp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU4OTUyMDEsImV4cCI6MjA4MTQ3MTIwMX0.qgSiHbYCWqo6qS9ZAsvwxk6aRr0Yq3LQnEp7XnRMHAc';

// Backend URL for booking creation (still uses localhost backend)
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Use Supabase Edge Function for Stripe payments, backend for bookings
const API_BASE_URL = process.env.REACT_APP_API_URL || SUPABASE_FUNCTION_URL;

const StripePaymentForm = ({ amount, amountNGN, onSuccess, onError, flight, passengerData, additionalOptions }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Format phone number to E.164 format
  const formatPhoneNumber = (phone, countryCode = null) => {
    // If phone is empty or just a country code, use default
    if (!phone || phone === '' || phone === null || phone === undefined) {
      const defaultPhone = countryCode ? (countryCode + '8012345678') : '+2348012345678';
      return String(defaultPhone);
    }
    
    // Ensure phone is a string and not an object
    let phoneStr = String(phone).trim();
    
    // If it's still an object string representation, use default
    if (phoneStr === '[object Object]' || phoneStr.includes('[object')) {
      const defaultPhone = countryCode ? (countryCode + '8012345678') : '+2348012345678';
      return String(defaultPhone);
    }
    
    // If phone already starts with +, check if it's complete
    if (phoneStr.startsWith('+')) {
      const digitsOnly = phoneStr.replace(/[^\d]/g, '');
      // If it's too short (just country code like +254, +234), it's incomplete
      if (digitsOnly.length <= 4) {
        // It's just a country code, use the country code selector and add default number
        if (countryCode) {
          phoneStr = countryCode.replace('+', '') + '8012345678';
        } else {
          phoneStr = '2348012345678'; // Default Nigeria
        }
      } else {
        // It's a complete number, use it as-is
        phoneStr = digitsOnly;
      }
    } else {
      // Remove all non-digits
      phoneStr = phoneStr.replace(/[^\d]/g, '');
      
      // Remove leading zeros
      phoneStr = phoneStr.replace(/^0+/, '');
      
      // If it starts with a country code (like 234, 254), check if we should use it or the selector
      if (phoneStr.startsWith('234') && phoneStr.length >= 13) {
        // Nigerian number with country code
        phoneStr = phoneStr;
      } else if (phoneStr.startsWith('254') && phoneStr.length >= 12) {
        // Kenyan number with country code
        phoneStr = phoneStr;
      } else if (countryCode) {
        // Use country code from selector and append the number
        const codeDigits = countryCode.replace('+', '').replace(/[^\d]/g, '');
        // Remove country code from phone if it's already there
        if (phoneStr.startsWith(codeDigits)) {
          phoneStr = phoneStr.substring(codeDigits.length);
        }
        phoneStr = codeDigits + phoneStr;
      } else {
        // Default to Nigeria
        phoneStr = '234' + phoneStr;
      }
    }
    
    // Ensure it starts with +
    if (!phoneStr.startsWith('+')) {
      phoneStr = '+' + phoneStr;
    }
    
    // Validate E.164 format: + followed by 1-15 digits (first digit after + must be 1-9)
    if (!/^\+[1-9]\d{1,14}$/.test(phoneStr)) {
      // Try to fix - remove leading zeros after +
      phoneStr = phoneStr.replace(/^\+0+/, '+');
      
      if (!/^\+[1-9]\d{1,14}$/.test(phoneStr)) {
        // Still invalid, use default
        return countryCode ? countryCode + '8012345678' : '+2348012345678';
      }
    }
    
    return phoneStr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Get the card elements
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        throw new Error('Card elements not found');
      }

      // Create payment intent on backend
      // Amount is always in NGN for Stripe
      let totalAmount = 0;

      // Prefer explicit NGN amount if provided
      if (amountNGN !== null && amountNGN !== undefined) {
        if (typeof amountNGN === 'string') {
          totalAmount = parseFloat(amountNGN.replace(/[₦$£€,]/g, '')) || 0;
        } else {
          totalAmount = parseFloat(amountNGN) || 0;
        }
      } else {
        // Backwards compatibility: derive NGN amount from display amount
        if (typeof amount === 'string') {
          // Remove all currency symbols and commas, then parse
          totalAmount = parseFloat(amount.replace(/[₦$£€,]/g, '')) || 0;
        } else {
          totalAmount = parseFloat(amount) || 0;
        }
      }
      
      // Validate amount
      if (!totalAmount || totalAmount <= 0) {
        throw new Error('Amount is required and must be greater than 0');
      }
      
      // Stripe amounts are in the smallest currency unit (kobo for NGN)
      // So multiply by 100 to convert NGN to kobo
      const amountInKobo = Math.round(totalAmount * 100);
      
      // Determine endpoint based on API base URL
      const endpoint = API_BASE_URL.includes('supabase.co') 
        ? `${API_BASE_URL}/stripe-payment`
        : `${API_BASE_URL}/api/stripe/create-payment-intent`;

      console.log('💳 Creating Stripe payment intent using:', endpoint);

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
      };

      // Always add Supabase anon key when using Supabase (required for authentication)
      if (API_BASE_URL.includes('supabase.co')) {
        if (SUPABASE_ANON_KEY) {
          headers['apikey'] = SUPABASE_ANON_KEY;
          headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
        }
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          amount: amountInKobo,
          currency: 'ngn'
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create payment intent');
      }

      // Create payment method first
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: passengerData?.firstName && passengerData?.lastName 
            ? `${passengerData.firstName} ${passengerData.lastName}`
            : 'Customer',
          email: passengerData?.email || '',
          phone: passengerData?.phone || '',
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Payment successful - create booking with Duffel
        try {
          // Format passengers for booking
          // Helper function to convert gender to Duffel format
          // Duffel expects: 'm' or 'f' (single letter)
          // Duffel requires gender to be non-empty, so we default to 'm' if not provided
          const formatGender = (gender) => {
            if (!gender || gender === '' || gender === null || gender === undefined) {
              return 'm'; // Default to 'm' if not provided (Duffel requires non-empty)
            }
            const genderLower = String(gender).toLowerCase().trim();
            // Map common gender values to Duffel format ('m' or 'f')
            if (genderLower === 'male' || genderLower === 'm' || genderLower === 'man') return 'm';
            if (genderLower === 'female' || genderLower === 'f' || genderLower === 'woman') return 'f';
            // If it's already in correct format, return it
            if (genderLower === 'm' || genderLower === 'f') {
              return genderLower;
            }
            // Default to 'm' for any unrecognized format
            console.warn(`Unrecognized gender format: "${gender}", defaulting to 'm'`);
            return 'm';
          };

          // Helper to split fullName into first and last name
          const splitName = (fullName) => {
            if (!fullName) return { first: 'John', last: 'Doe' };
            const parts = fullName.trim().split(/\s+/);
            if (parts.length === 1) return { first: parts[0], last: 'Doe' };
            return { first: parts[0], last: parts.slice(1).join(' ') };
          };

          const nameParts = splitName(passengerData?.fullName || passengerData?.firstName);
          
          // Helper to format title to lowercase (Duffel expects: 'mr', 'mrs', 'ms', 'miss')
          const formatTitle = (title) => {
            if (!title || title === '' || title === null || title === undefined) {
              return 'mr'; // Default to 'mr' if not provided (Duffel requires non-empty)
            }
            const titleLower = String(title).toLowerCase().trim();
            // Map common title values
            if (titleLower === 'mr' || titleLower === 'mister') return 'mr';
            if (titleLower === 'mrs' || titleLower === 'missus') return 'mrs';
            if (titleLower === 'ms') return 'ms';
            if (titleLower === 'miss') return 'miss';
            return titleLower; // Return as-is if already in correct format
          };

          // Extract phone number - handle both string and object cases
          let rawPhone = passengerData.phone || passengerData.phoneNumber;
          // If it's an object, try to extract the value
          if (rawPhone && typeof rawPhone === 'object') {
            rawPhone = rawPhone.value || rawPhone.number || rawPhone.phone || null;
          }
          // Get country code
          let countryCode = passengerData.phoneCountryCode || '+234';
          if (countryCode && typeof countryCode === 'object') {
            countryCode = countryCode.value || countryCode.code || '+234';
          }
          
          // Format phone number once and ensure it's a string
          const formattedPhoneForPassenger = String(formatPhoneNumber(
            rawPhone,
            countryCode
          ));
          
          const passengers = passengerData && Object.keys(passengerData).length > 0
            ? [{
                type: 'adult',
                given_name: String(passengerData.firstName || nameParts.first || ''),
                family_name: String(passengerData.lastName || nameParts.last || ''),
                email: String(passengerData.email || 'customer@example.com'),
                phone_number: String(formattedPhoneForPassenger), // Ensure it's always a string
                title: formatTitle(passengerData.title), // Always returns a valid title
                gender: formatGender(passengerData.gender), // Always returns a valid gender
                // Only include born_on if provided and valid (not in future)
                ...(passengerData.dateOfBirth && (() => {
                  const birthDate = new Date(passengerData.dateOfBirth);
                  const today = new Date();
                  today.setHours(23, 59, 59, 999);
                  if (birthDate <= today) {
                    return { born_on: passengerData.dateOfBirth };
                  }
                  return {}; // Don't include if invalid
                })())
              }]
            : [{
                type: 'adult',
                given_name: 'John',
                family_name: 'Doe',
                email: 'customer@example.com',
                phone_number: '+2348012345678',
                title: 'mr', // Default title
                gender: 'm' // Default gender (Duffel expects 'm' or 'f')
              }];

          // Check if we have a valid Duffel offer ID
          const offerId = flight.id || flight.offerId;
          
          console.log('🔵 StripePaymentForm - Using offer ID for booking:', offerId);
          console.log('🔵 StripePaymentForm - Flight travelClass:', flight.travelClass);
          console.log('🔵 StripePaymentForm - Flight cabinClass:', flight.cabinClass);
          console.log('🔵 StripePaymentForm - Flight id:', flight.id);
          console.log('🔵 StripePaymentForm - Flight offerId:', flight.offerId);

          if (!offerId) {
            throw new Error('No flight offer ID found. Please search for flights first.');
          }

          // Check if offerId looks like a valid Duffel offer ID (starts with 'off_')
          if (!offerId.toString().startsWith('off_')) {
            // If it's not a valid Duffel offer ID, skip booking creation
            // This might be a mock/test flight
            console.warn('Invalid Duffel offer ID:', offerId, '- Skipping booking creation');
            
            // Still show success since payment succeeded
            onSuccess({
              booking: null,
              payment: {
                paymentIntentId: paymentIntent.id,
                paymentMethod: 'stripe',
                amount: paymentIntent.amount / 100,
                currency: paymentIntent.currency,
                status: 'succeeded'
              },
              message: 'Payment successful, but booking could not be created with test flight data. Please use a real flight search result.'
            });
            return;
          }

          // Prepare contacts array from passenger data
          // Extract phone number - handle both string and object cases
          let rawContactPhone = passengers[0].phone_number || passengerData?.phoneNumber || passengerData?.phone;
          if (rawContactPhone && typeof rawContactPhone === 'object') {
            rawContactPhone = rawContactPhone.value || rawContactPhone.number || rawContactPhone.phone || null;
          }
          // Get country code for contact
          let contactCountryCode = passengerData?.phoneCountryCode || '+234';
          if (contactCountryCode && typeof contactCountryCode === 'object') {
            contactCountryCode = contactCountryCode.value || contactCountryCode.code || '+234';
          }
          
          // Ensure phone number is properly formatted in E.164 format and is a string
          const formattedPhoneForContact = String(formatPhoneNumber(
            rawContactPhone,
            contactCountryCode
          ));
          const contacts = [{
            email: String(passengers[0].email || ''),
            phone_number: String(formattedPhoneForContact) // Ensure it's always a string
          }];
          
          // Final validation - ensure phone_number is a valid string (not object)
          if (typeof contacts[0].phone_number !== 'string' || contacts[0].phone_number === '[object Object]' || contacts[0].phone_number.includes('[object')) {
            console.error('[StripePaymentForm] Invalid phone_number in contacts, using default');
            contacts[0].phone_number = '+2348012345678';
          }
          if (passengers[0] && (typeof passengers[0].phone_number !== 'string' || passengers[0].phone_number === '[object Object]' || passengers[0].phone_number.includes('[object'))) {
            console.error('[StripePaymentForm] Invalid phone_number in passengers, using default');
            passengers[0].phone_number = '+2348012345678';
          }

          console.log('[StripePaymentForm] Creating booking with:', {
            offerId,
            passengersCount: passengers.length,
            passengers: passengers.map(p => ({
              type: p.type,
              given_name: p.given_name,
              family_name: p.family_name,
              email: p.email,
              phone_number: p.phone_number,
              phone_number_type: typeof p.phone_number,
              title: p.title,
              gender: p.gender,
              gender_type: typeof p.gender,
              hasId: !!p.id
            })),
            contacts: contacts.map(c => ({
              email: c.email,
              phone_number: c.phone_number,
              phone_number_type: typeof c.phone_number
            })),
            paymentType: 'stripe',
            stripePaymentIntentId: paymentIntent.id
          });
          
          // Log full payload being sent
          const bookingPayload = {
            offerId,
            passengers,
            contacts,
            paymentType: 'stripe',
            stripePaymentIntentId: paymentIntent.id
          };
          console.log('[StripePaymentForm] Full booking payload:', JSON.stringify(bookingPayload, null, 2));

          // Create booking with Stripe payment intent
          // Use Supabase Edge Function for booking creation
          const bookingEndpoint = API_BASE_URL.includes('supabase.co') 
            ? `${API_BASE_URL}/create-booking`
            : `${BACKEND_URL}/api/bookings/create`;
          
          console.log('📦 Creating booking using:', bookingEndpoint);

          // Prepare booking headers
          const bookingHeaders = {
            'Content-Type': 'application/json',
          };

          // Add Supabase auth headers if using Supabase
          if (bookingEndpoint.includes('supabase.co')) {
            if (SUPABASE_ANON_KEY) {
              bookingHeaders['apikey'] = SUPABASE_ANON_KEY;
              bookingHeaders['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
            }
          }

          // Create clean payload with explicitly stringified phone numbers
          const cleanPassengers = passengers.map(p => ({
            ...p,
            phone_number: String(p.phone_number || '+2348012345678'),
            given_name: String(p.given_name || ''),
            family_name: String(p.family_name || ''),
            email: String(p.email || ''),
            title: String(p.title || 'mr'),
            gender: String(p.gender || 'm'),
            type: String(p.type || 'adult')
          }));
          
          const cleanContacts = contacts.map(c => ({
            email: String(c.email || ''),
            phone_number: String(c.phone_number || '+2348012345678')
          }));
          
          const cleanPayload = {
            offerId: String(offerId),
            passengers: cleanPassengers,
            contacts: cleanContacts,
            paymentType: 'stripe',
            stripePaymentIntentId: String(paymentIntent.id)
          };
          
          console.log('[StripePaymentForm] Clean payload phone numbers:', {
            passengers: cleanPassengers.map(p => ({ phone: p.phone_number, type: typeof p.phone_number })),
            contacts: cleanContacts.map(c => ({ phone: c.phone_number, type: typeof c.phone_number }))
          });

          const bookingResponse = await fetch(bookingEndpoint, {
            method: 'POST',
            headers: bookingHeaders,
            body: JSON.stringify(cleanPayload),
          });

          const bookingData = await bookingResponse.json();

          // Log booking confirmation
          if (bookingData.success && bookingData.booking) {
            console.log('[StripePaymentForm] ========== BOOKING CONFIRMED ==========');
            console.log('[StripePaymentForm] Order ID:', bookingData.booking.id);
            console.log('[StripePaymentForm] Booking Reference:', bookingData.booking.bookingReference);
            console.log('[StripePaymentForm] Status:', bookingData.booking.status);
            console.log('[StripePaymentForm] Total Amount:', bookingData.booking.totalAmount, bookingData.booking.currency);
            console.log('[StripePaymentForm] Duffel Order ID:', bookingData.booking.duffelOrderId);
            console.log('[StripePaymentForm] Created At:', bookingData.booking.created_at);
            console.log('[StripePaymentForm] ======================================');
          }

          if (!bookingData.success) {
            // Log detailed error information for debugging
            console.error('[StripePaymentForm] Booking failed:', {
              error: bookingData.error,
              details: bookingData.details,
              errors: bookingData.errors,
              debug: bookingData.debug
            });

            // Build a clean, user‑friendly error message (avoid [object Object])
            let errorMessage = bookingData.error || 'Failed to create booking';

            // If Duffel/Supabase provided a request_id, surface it explicitly
            const requestId =
              bookingData.details?.error?.request_id ||
              bookingData.details?.request_id ||
              bookingData.debug?.request_id;

            if (requestId) {
              errorMessage += ` (request ID: ${requestId})`;
            }

            // Append any field‑level validation errors in a readable way
            if (bookingData.errors && Array.isArray(bookingData.errors)) {
              const errorList = bookingData.errors.map(e =>
                typeof e === 'string'
                  ? e
                  : `${e.field || 'unknown'}: ${e.message || e.detail || ''}`.trim()
              ).join('; ');
              if (errorList) {
                errorMessage += ` — ${errorList}`;
              }
            }

            throw new Error(errorMessage);
          }

          // Save booking data to Supabase
          try {
            console.log('💾 ========== SAVING TO SUPABASE ==========');
            console.log('💾 Saving booking data to Supabase...');
            
            // Get user ID from localStorage if available
            let userId = null;
            try {
              const userData = localStorage.getItem('user');
              if (userData) {
                const user = JSON.parse(userData);
                userId = user.id || null;
                console.log('👤 User ID from localStorage:', userId);
              } else {
                console.log('👤 No user logged in (guest booking)');
              }
            } catch (error) {
              console.warn('⚠️ Could not get user ID:', error);
            }
            
            const supabaseResult = await saveBookingToSupabase({
              bookingReference: bookingData.booking?.bookingReference || bookingData.booking?.id,
              offerId: offerId,
              flight: flight,
              passengers: passengers,
              passengerData: passengerData,
              payment: {
                paymentIntentId: paymentIntent.id,
                paymentMethod: 'stripe',
                amount: paymentIntent.amount / 100,
                currency: paymentIntent.currency,
                status: 'succeeded'
              },
              paymentMethod: 'stripe',
              additionalOptions: additionalOptions || {},
              duffelBooking: bookingData.booking,
              userId: userId
            });
            
            console.log('✅ ========== SUPABASE SAVE SUCCESS ==========');
            console.log('✅ Booking saved to Supabase:', supabaseResult);
            console.log('✅ Booking Reference:', supabaseResult.bookingReference);
            console.log('✅ Booking ID:', supabaseResult.booking?.id);

            // Send booking confirmation email
            try {
              console.log('[Email] 📧 Attempting to send booking confirmation email...');
              const customerEmail = passengers[0]?.email || passengerData?.email;
              const customerName = passengers[0] 
                ? `${passengers[0].given_name || ''} ${passengers[0].family_name || ''}`.trim()
                : passengerData 
                  ? `${passengerData.firstName || ''} ${passengerData.lastName || ''}`.trim()
                  : 'Valued Customer';

              if (customerEmail) {
                console.log('[Email] Customer email:', customerEmail);
                console.log('[Email] Customer name:', customerName);
                
                // Get flight slices from duffelBooking or flight object
                const flightSlices = bookingData.booking?.slices || bookingData.booking?.rawOrder?.slices || 
                                  (flight?.slices ? flight.slices : []) ||
                                  (flight?.outbound ? [{ segments: flight.outbound.segments || [] }] : []);
                
                const emailPayload = {
                  customerEmail,
                  customerName,
                  bookingReference: bookingData.booking?.bookingReference || bookingData.booking?.id || supabaseResult.bookingReference,
                  orderId: bookingData.booking?.id || bookingData.booking?.duffelOrderId || bookingData.booking?.orderId,
                  passengers: passengers || [],
                  slices: flightSlices,
                  totalAmount: bookingData.booking?.totalAmount || amount || 0,
                  currency: bookingData.booking?.currency || 'NGN',
                  status: bookingData.booking?.status || 'confirmed'
                };

                console.log('[Email] Email payload:', JSON.stringify(emailPayload, null, 2));
                
                const emailResponse = await fetch(`${BACKEND_URL}/api/bookings/send-confirmation-email`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(emailPayload)
                });

                const emailResult = await emailResponse.json();
                
                if (emailResult.success) {
                  console.log('[Email] ✅ Booking confirmation email sent successfully');
                  console.log('[Email] Email ID:', emailResult.emailId);
                } else {
                  console.warn('[Email] ⚠️ Email sending failed:', emailResult.error);
                }
              } else {
                console.warn('[Email] ⚠️ No customer email found, skipping email');
              }
            } catch (emailError) {
              // Don't fail the booking if email fails
              console.error('[Email] ❌ Error sending confirmation email:', emailError);
            }
          } catch (supabaseError) {
            console.error('❌ ========== SUPABASE SAVE FAILED ==========');
            console.error('❌ Failed to save booking to Supabase:', supabaseError);
            console.error('❌ Error message:', supabaseError.message);
            console.error('❌ Full error:', supabaseError);
            const ref = bookingData.booking?.bookingReference || bookingData.booking?.id;
            const detail = supabaseError?.message ? ` Details: ${supabaseError.message}` : '';
            alert(
              `⚠️ Booking succeeded with the airline, but saving to our database failed. Reference: ${ref}.${detail} If this keeps happening, check Supabase RLS policies for flight_bookings (allow anon INSERT) or open the browser console for the full error.`
            );
            // Don't fail the entire flow if Supabase save fails
            // The booking was already created with Duffel
          }

          // Call success handler with booking and payment data
          onSuccess({
            booking: bookingData.booking,
            payment: {
              paymentIntentId: paymentIntent.id,
              paymentMethod: 'stripe',
              amount: paymentIntent.amount / 100,
              currency: paymentIntent.currency,
              status: 'succeeded'
            }
          });
        } catch (bookingError) {
          // Payment succeeded but booking failed
          setError(`Payment successful but booking failed: ${bookingError.message}`);
          throw bookingError;
        }
      } else {
        throw new Error('Payment not completed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during payment');
      if (onError) {
        onError(err);
      }
    } finally {
      setProcessing(false);
    }
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'Inter, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="form-group">
        <label className="form-label">
          Card Number <span className="required">*</span>
        </label>
        <div className="stripe-card-number-element">
          <CardNumberElement options={elementOptions} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">
            Expiry Date <span className="required">*</span>
          </label>
          <div className="stripe-card-expiry-element">
            <CardExpiryElement options={elementOptions} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            CVV <span className="required">*</span>
          </label>
          <div className="stripe-card-cvc-element">
            <CardCvcElement options={elementOptions} />
          </div>
        </div>
      </div>

      {error && (
        <div className="stripe-error" role="alert">
          {error}
        </div>
      )}

      <button
        type="submit"
        className="pay-now-button"
        disabled={!stripe || processing}
      >
        {processing ? 'Processing...' : `Pay ${amount}`}
      </button>
    </form>
  );
};

export default StripePaymentForm;
